import { useState } from 'react'
import { Outlet, NavLink, useNavigate } from 'react-router-dom'
import { useMsal } from '@azure/msal-react'
import {
  IconButton,
  Persona,
  PersonaSize,
  ContextualMenu,
  IContextualMenuItem,
  TooltipHost,
} from '@fluentui/react'
import {
  Home24Regular,
  Apps24Regular,
  People24Regular,
  Key24Regular,
  Building24Regular,
  History24Regular,
  DataTrending24Regular,
  WeatherMoon24Regular,
  WeatherSunny24Regular,
} from '@fluentui/react-icons'
import { useTheme } from '../context/ThemeContext'
import { SsopsLogo } from './SsopsLogo'
import { useRoles } from '../hooks/useRoles'
import { useActiveAccount } from '../hooks/useActiveAccount'
import { accountPickerRequest } from '../config/authConfig'

const navItems = [
  { path: '/', label: 'Dashboard', icon: <Home24Regular /> },
  { path: '/applications', label: 'Applications', icon: <Apps24Regular /> },
  { path: '/credentials', label: 'Credentials', icon: <Key24Regular /> },
  { path: '/owners', label: 'Owners', icon: <People24Regular /> },
  { path: '/business-units', label: 'Business Units', icon: <Building24Regular /> },
  { path: '/reports', label: 'Reports', icon: <DataTrending24Regular /> },
  { path: '/audit', label: 'Audit Log', icon: <History24Regular /> },
]

export function Layout() {
  const { instance } = useMsal()
  const account = useActiveAccount()
  const navigate = useNavigate()
  const { isDarkMode, toggleTheme } = useTheme()
  const { isAdmin, isViewer } = useRoles()
  const [isNavCollapsed, setIsNavCollapsed] = useState(false)
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [userMenuTarget, setUserMenuTarget] = useState<HTMLElement | null>(null)

  const userName = account?.name || account?.username || 'User'
  const userEmail = account?.username || ''
  const roleLabel = isAdmin ? 'Administrator' : isViewer ? 'Viewer' : 'No role'

  const handleLogout = () => {
    instance.logoutRedirect()
  }

  const handleSwitchAccount = () => {
    // Clear the active-account pointer before redirecting so that whatever
    // account the user picks at the Microsoft picker becomes the unambiguous
    // active account on return. Without this, MSAL's cache still contains the
    // old active-account hint and the post-redirect code path could race.
    instance.setActiveAccount(null)
    instance.loginRedirect(accountPickerRequest)
  }

  const userMenuItems: IContextualMenuItem[] = [
    ...(isAdmin
      ? [{
          key: 'settings',
          text: 'Settings',
          iconProps: { iconName: 'Settings' },
          onClick: () => { navigate('/settings'); return undefined },
        }]
      : []),
    {
      key: 'switchAccount',
      text: 'Switch account',
      iconProps: { iconName: 'Switch' },
      onClick: () => { handleSwitchAccount(); return undefined },
    },
    {
      key: 'divider',
      itemType: 1, // Divider
    },
    {
      key: 'logout',
      text: 'Sign out',
      iconProps: { iconName: 'SignOut' },
      onClick: () => { handleLogout(); return undefined },
    },
  ]

  return (
    <div style={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Skip link — visible only on keyboard focus (WCAG 2.4.1) */}
      <a href="#main-content" className="skip-link">Skip to main content</a>

      {/* Sidebar */}
      <nav
        aria-label="Main navigation"
        style={{
          width: isNavCollapsed ? 60 : 240,
          backgroundColor: 'var(--sidebar-bg)',
          color: 'var(--sidebar-text)',
          transition: 'width 0.2s ease, background-color 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          zIndex: 1000,
        }}
      >
        {/* Header */}
        <header
          style={{
            padding: '16px',
            borderBottom: '1px solid var(--sidebar-border)',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
          }}
        >
          <IconButton
            iconProps={{ iconName: 'GlobalNavButton' }}
            ariaLabel={isNavCollapsed ? 'Expand navigation' : 'Collapse navigation'}
            styles={{
              root: { color: 'var(--sidebar-text)' },
              rootHovered: { backgroundColor: 'var(--sidebar-hover)' },
            }}
            onClick={() => setIsNavCollapsed(!isNavCollapsed)}
          />
          {isNavCollapsed ? (
            <SsopsLogo markOnly size={24} />
          ) : (
            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <SsopsLogo size={26} />
            </div>
          )}
          {!isNavCollapsed && (
            <TooltipHost content={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}>
              <IconButton
                ariaLabel={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
                onClick={toggleTheme}
                styles={{
                  root: { color: 'var(--sidebar-text)' },
                  rootHovered: { backgroundColor: 'var(--sidebar-hover)' },
                }}
              >
                {isDarkMode ? <WeatherSunny24Regular /> : <WeatherMoon24Regular />}
              </IconButton>
            </TooltipHost>
          )}
        </header>

        {/* Navigation Items */}
        <div style={{ flex: 1, padding: '8px 0' }}>
          {navItems.map(item => (
            <NavLink
              key={item.path}
              to={item.path}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                padding: '12px 16px',
                color: isActive ? 'var(--sidebar-text)' : 'var(--sidebar-text-muted)',
                backgroundColor: isActive ? 'var(--sidebar-hover)' : 'transparent',
                textDecoration: 'none',
                borderLeft: isActive ? '3px solid var(--sidebar-active-border)' : '3px solid transparent',
                transition: 'all 0.15s ease',
              })}
            >
              {item.icon}
              {!isNavCollapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </div>

        {/* User Section */}
        <div
          style={{
            padding: '16px',
            borderTop: '1px solid var(--sidebar-border)',
          }}
        >
          <button
            ref={el => setUserMenuTarget(el)}
            onClick={() => setShowUserMenu(true)}
            aria-label={`${userName} — ${roleLabel}. Open account menu.`}
            aria-expanded={showUserMenu}
            aria-haspopup="menu"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '4px',
              background: 'none',
              border: 'none',
              width: '100%',
              textAlign: 'left',
            }}
          >
            <Persona
              text={userName}
              secondaryText={!isNavCollapsed ? userEmail : undefined}
              tertiaryText={!isNavCollapsed ? roleLabel : undefined}
              size={PersonaSize.size32}
              hidePersonaDetails={isNavCollapsed}
              showSecondaryText={!isNavCollapsed}
              initialsTextColor="#ffffff"
              styles={{
                primaryText: { color: 'var(--sidebar-text)' },
                secondaryText: { color: 'var(--sidebar-text-muted)' },
                tertiaryText: { color: 'var(--sidebar-text-muted)' },
              }}
            />
          </button>
          {showUserMenu && (
            <ContextualMenu
              items={userMenuItems}
              target={userMenuTarget}
              onDismiss={() => setShowUserMenu(false)}
            />
          )}
        </div>
      </nav>

      {/* Main Content */}
      <main
        id="main-content"
        style={{
          flex: 1,
          backgroundColor: 'var(--background-color)',
          overflow: 'auto',
          transition: 'background-color 0.2s ease',
        }}
      >
        <Outlet />
      </main>
    </div>
  )
}
